'use client'

import React, { useEffect, useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { BarChart3 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { MiniChart } from '@/components/mini-chart'
import { StockChartDataPoint } from '@/types/stock-data'
import { getStockChartDataAction } from '@/actions/stock-data'
import { mapLegacyTimeframeToNew } from '@/lib/utils/stock-data-transform'
import { StockDetailDialog } from '@/components/stock-detail-dialog'
import type { StockCardProps, StockPriceData, KeyMetrics } from '@/types/stock-card'

const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(price)
}

const formatVolume = (volume: number): string => {
    if (volume >= 1e9) return `${(volume / 1e9).toFixed(1)}B`
    if (volume >= 1e6) return `${(volume / 1e6).toFixed(1)}M`
    if (volume >= 1e3) return `${(volume / 1e3).toFixed(1)}K`
    return volume.toLocaleString()
}

const calculatePriceChange = (
    data: StockPriceData[]
): { change: number; changePercent: number } => {
    if (data.length < 2) return { change: 0, changePercent: 0 }

    const latest = data[0]
    const previous = data[1]

    if (!latest || !previous) return { change: 0, changePercent: 0 }

    const latestPrice = latest.adjusted_close
    const previousPrice = previous.adjusted_close

    if (previousPrice === 0) return { change: 0, changePercent: 0 }

    const change = latestPrice - previousPrice
    const changePercent = (change / previousPrice) * 100

    return { change, changePercent }
}

const convertToEodData = (data: StockPriceData[]): StockChartDataPoint[] => {
    return data.map((item) => ({
        date: item.date,
        trade_date: item.date,
        open: item.open,
        high: item.high,
        low: item.low,
        close: item.close,
        adjusted_open: item.adjusted_open,
        adjusted_high: item.adjusted_high,
        adjusted_low: item.adjusted_low,
        adjusted_close: item.adjusted_close,
        volume: item.volume,
    }))
}

// Demo key metrics data
const defaultKeyMetrics: KeyMetrics = {
    marketCap: 'N/A',
    pe: 'N/A',
    roe: 'N/A',
    evSales: 'N/A',
    roa: 'N/A',
    fcfYield: 'N/A',
}

export default function StockCard({
    symbol,
    companyId,
    found,
    latest_price,
    historical_data,
    keyMetrics = defaultKeyMetrics,
    companyName,
    className,
    showChart = true,
    chartHeight = 130,
    chartWidth = 236,
    showOHLC = false,
    showVolume = false,
    period = '1Y',
    exchange = 'NASDAQ',
}: StockCardProps) {
    const [chartData, setChartData] = useState<StockChartDataPoint[]>([])
    const [isLoadingChart, setIsLoadingChart] = useState(false)
    const [chartError, setChartError] = useState<string | null>(null)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [preventParentClick, setPreventParentClick] = useState(false)

    // Sync historical_data prop to internal state when provided (for watchlist context)
    // This ensures the chart updates when period changes and new data is passed from parent
    useEffect(() => {
        if (historical_data.length > 0) {
            // Check if data is already StockChartDataPoint[] (has trade_date) or needs conversion
            const firstItem = historical_data[0]
            if (firstItem && 'trade_date' in firstItem && firstItem.trade_date !== undefined) {
                // Already StockChartDataPoint[], use directly
                setChartData(historical_data as unknown as StockChartDataPoint[])
            } else {
                // StockPriceData[], convert to StockChartDataPoint[]
                const convertedData = convertToEodData(historical_data)
                setChartData(convertedData)
            }
        }
    }, [historical_data])

    // Fetch chart data when period changes (only if no historical_data provided)
    useEffect(() => {
        const fetchChartData = async () => {
            // Only fetch data if no historical_data is provided (i.e., when used in watchlist)
            // If historical_data is provided (i.e., when used in chat), use that data instead
            if (!companyId || !showChart || historical_data.length > 0) return

            setIsLoadingChart(true)
            setChartError(null)

            try {
                const timeframe = mapLegacyTimeframeToNew(period)
                const response = await getStockChartDataAction({ companyId, timeframe })
                setChartData(response.data)
            } catch (error) {
                const errorMessage = `Failed to load chart data for company ID ${companyId}: ${error instanceof Error ? error.message : 'Unknown error'}`
                setChartError(errorMessage)
                setChartData([])
            } finally {
                setIsLoadingChart(false)
            }
        }

        fetchChartData()
    }, [companyId, period, showChart, historical_data.length])

    // Calculate price change from historical_data (prop) to ensure consistent SSR/hydration
    // Use useMemo to prevent recalculation on every render and ensure consistency
    // Must be called before any conditional returns to follow React hooks rules
    const { change, changePercent } = useMemo(() => {
        // Always use historical_data prop for price change calculation to ensure
        // server and client render the same value (avoiding hydration mismatches)
        if (historical_data.length >= 2) {
            // Historical data is typically ordered by date descending (newest first)
            // Use calculatePriceChange which handles this correctly
            return calculatePriceChange(historical_data)
        }
        return { change: 0, changePercent: 0 }
    }, [historical_data])
    const isPositive = change >= 0
    const eodData = convertToEodData(historical_data)

    if (!found) {
        return (
            <Card className={cn('w-full max-w-2xl', className)}>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-destructive">
                        <BarChart3 className="h-5 w-5" />
                        Stock Not Found
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-muted-foreground">
                        No data available for symbol: <Badge variant="outline">{symbol}</Badge>
                    </p>
                </CardContent>
            </Card>
        )
    }

    return (
        <Card
            className={cn(
                'w-full max-w-[260px] max-h-[213px] bg-transparent gap-0 border border-black/10 dark:border-white/10 p-2.5',
                className
            )}
            onClick={(e) => {
                // Prevent parent click if dialog interaction is active
                if (preventParentClick) {
                    e.stopPropagation()
                }
            }}
        >
            <CardHeader className="p-0">
                {/* Header - Company Info and Performance */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                        <div>
                            <div
                                className="text-black/80 dark:text-white/80 text-callout-medium cursor-pointer hover:text-black dark:hover:text-white transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setPreventParentClick(true)
                                    setIsDialogOpen(true)
                                }}
                            >
                                {symbol}
                            </div>
                            <div
                                title={companyName || 'Company Name'}
                                className="text-subheadline truncate max-w-[180px] text-black/40 dark:text-white/40"
                            >
                                {companyName || 'Company Name'}
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div
                            className={`text-callout-medium ${
                                isPositive ? 'text-green-500' : 'text-red-500'
                            }`}
                        >
                            {changePercent >= 0 ? '+' : ''}
                            {changePercent.toFixed(2)}%
                        </div>
                        <div
                            className={`text-callout-medium ${
                                isPositive ? 'text-green-500' : 'text-red-500'
                            }`}
                        >
                            {change >= 0 ? '+' : ''}
                            {change.toFixed(2)}
                        </div>
                    </div>
                </div>
            </CardHeader>

            <CardContent className="space-y-6 p-0">
                {/* Key Metrics Section */}
                <div className="flex justify-between gap-4">
                    {/* Key Metrics - Left */}
                    <div className="flex-1 space-y-2">
                        <div className="space-y-1">
                            <div className="flex justify-between items-center">
                                <span className="text-subheadline-medium text-black/40 dark:text-white/40">
                                    Mkt Cap
                                </span>
                                <span className="text-subheadline-medium text-black/60 dark:text-white/60">
                                    {keyMetrics.marketCap}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-subheadline-medium text-black/40 dark:text-white/40">
                                    P/E
                                </span>
                                <span className="text-subheadline-medium text-black/60 dark:text-white/60">
                                    {keyMetrics.pe}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-subheadline-medium text-black/40 dark:text-white/40">
                                    ROE
                                </span>
                                <span className="text-subheadline-medium text-black/60 dark:text-white/60">
                                    {keyMetrics.roe}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="w-px bg-border/30"></div>

                    {/* Key Metrics - Right */}
                    <div className="flex-1 space-y-2">
                        <div className="space-y-1">
                            <div className="flex justify-between items-center">
                                <span className="text-subheadline-medium text-black/40 dark:text-white/40">
                                    EV/Sales
                                </span>
                                <span className="text-subheadline-medium text-black/60 dark:text-white/60">
                                    {keyMetrics.evSales}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-subheadline-medium text-black/40 dark:text-white/40">
                                    ROA
                                </span>
                                <span className="text-subheadline-medium text-black/60 dark:text-white/60">
                                    {keyMetrics.roa}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-subheadline-medium text-black/40 dark:text-white/40">
                                    FCF Yield
                                </span>
                                <span className="text-subheadline-medium text-black/60 dark:text-white/60">
                                    {keyMetrics.fcfYield}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Price Details */}
                {(showOHLC || showVolume) && (
                    <div
                        className={cn(
                            'grid gap-4',
                            showOHLC && showVolume
                                ? 'grid-cols-2 md:grid-cols-4'
                                : showOHLC
                                  ? 'grid-cols-3'
                                  : showVolume
                                    ? 'grid-cols-1'
                                    : ''
                        )}
                    >
                        {showOHLC && (
                            <>
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground">Open</p>
                                    <p className="font-medium">{formatPrice(latest_price.open)}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground">High</p>
                                    <p className="font-medium">{formatPrice(latest_price.high)}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground">Low</p>
                                    <p className="font-medium">{formatPrice(latest_price.low)}</p>
                                </div>
                            </>
                        )}
                        {showVolume && (
                            <div className="space-y-1">
                                <p className="text-sm text-muted-foreground">Volume</p>
                                <p className="font-medium">{formatVolume(latest_price.volume)}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Chart */}
                {showChart && (
                    <div className="space-y-2">
                        {chartData.length > 0 ? (
                            // Use fetched chart data (watchlist context) - this is preferred
                            <MiniChart
                                companyId={companyId || 0}
                                symbol={symbol}
                                eodData={chartData}
                                width={chartWidth}
                                height={chartHeight}
                                period={period}
                            />
                        ) : historical_data.length > 0 ? (
                            // Use provided historical data (from chat/Ticker)
                            <MiniChart
                                companyId={companyId || 0}
                                symbol={symbol}
                                eodData={eodData}
                                width={chartWidth}
                                height={chartHeight}
                                period={period}
                            />
                        ) : isLoadingChart ? (
                            // Show skeleton when fetching data (watchlist context)
                            <Skeleton
                                className="rounded-sm"
                                style={{ width: `${chartWidth}px`, height: `${chartHeight}px` }}
                            />
                        ) : chartError ? (
                            // Show error state
                            <div
                                className="flex items-center justify-center"
                                style={{ height: `${chartHeight}px`, width: `${chartWidth}px` }}
                            >
                                <div className="text-sm text-destructive">Chart error</div>
                            </div>
                        ) : (
                            // No data available
                            <div
                                className="flex items-center justify-center"
                                style={{ height: `${chartHeight}px`, width: `${chartWidth}px` }}
                            >
                                <div className="text-sm text-muted-foreground">No chart data</div>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>

            {/* Stock Detail Dialog */}
            <StockDetailDialog
                open={isDialogOpen}
                onOpenChange={(open) => {
                    setIsDialogOpen(open)
                    // Clear the flag after dialog closes with a longer delay
                    if (!open) {
                        setTimeout(() => {
                            setPreventParentClick(false)
                        }, 300)
                    }
                }}
                symbol={symbol}
                exchange={exchange}
            />
        </Card>
    )
}
